/* eslint-disable no-useless-constructor */
import get from 'lodash/get';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { clearStore } from './actions/AppAction';
import { setNetworkStatus } from './actions/NetworkStatusActions';
import {
  startNotificationPullingSystem,
  startNotificationPullingTimer,
} from './actions/NotificationsActions';
import { getTranslatedKey } from './actions/TranslationActions';
import ScrollToTop from './components/general/ScrollToTop';
import './assets/css/styles.css';
import IdpDialog from './components/IdpDialog';
import LoginDialog from './components/LoginDialog';
import { SessionTimeoutWarning } from './components/SessionTimeout';
import StorageSpaceUnavailableDialog from './components/StorageSpaceUnavailableDialog';
import ToastNotifications from './components/ToastNotifications';
import * as Urls from './constants/Urls';
import NotificationDialog from './containers/NotificationDialog';
import Routes from './Routes';
import { getPagePathname } from './utils/childrenWindowsUtils';
import { setGlobalMomentLocale } from './utils/dateUtils';
import { getEnvVar, loadEnvVars } from './utils/env';
import '@khmyznikov/pwa-install';
import { parseBoolean } from './utils/fns';
import { logUIVersion } from './utils/log';

class App extends Component {
  constructor(props, context) {
    super(props, context);
    this.handleNetworkStatusChange = this.handleNetworkStatusChange.bind(this);
    this.showPWAInstallMessage = this.showPWAInstallMessage.bind(this);
  }

  componentDidMount() {
    const { startNotificationPullingSystem, isLoggedIn, getTranslatedKey } = this.props;

    const pathname = getPagePathname();

    if (isLoggedIn && pathname !== Urls.LOGIN) {
      startNotificationPullingSystem();
      getTranslatedKey(localStorage.getItem('sessionExpirationErrorCode'));
    }

    loadEnvVars();
    setGlobalMomentLocale();
    logUIVersion();

    window.addEventListener('offline', this.handleNetworkStatusChange);
    window.addEventListener('online', this.handleNetworkStatusChange);

    const pwaInstallElement = document.getElementById('pwa-install');

    pwaInstallElement?.addEventListener('pwa-user-choice-result-event', () => {
      localStorage.setItem('pwaInstallMessageShowed', 'true');
    });

    const timeout = setTimeout(this.showPWAInstallMessage, 500);
    this.setState({ timeout });
  }

  componentWillUnmount() {
    window.removeEventListener('offline', this.handleNetworkStatusChange);
    window.removeEventListener('online', this.handleNetworkStatusChange);

    const pwaInstallElement = document.getElementById('pwa-install');
    pwaInstallElement?.removeEventListener('pwa-user-choice-result-event');

    clearTimeout(this.timeout);
  }

  componentDidUpdate(prevProps) {
    const {
      isLoggedIn,
      clearStore,
      startNotificationPullingSystem,
      startNotificationPullingTimer,
      isTokenExpired,
      isLogonUserChanged,
    } = this.props;
    const justLoggedOut = prevProps.isLoggedIn !== isLoggedIn && !isLoggedIn;
    const justLoggedIn = prevProps.isLoggedIn !== isLoggedIn && isLoggedIn;
    const justTokenUpdatedAfterExpiration =
      prevProps.isTokenExpired !== isTokenExpired && !isTokenExpired;

    const notLoginScreen = getPagePathname() !== Urls.LOGIN;
    if (notLoginScreen) {
      if (justLoggedIn || isLogonUserChanged) {
        startNotificationPullingSystem();
      }

      if (isLoggedIn && justTokenUpdatedAfterExpiration && !isLogonUserChanged) {
        startNotificationPullingTimer();
      }
    }

    setGlobalMomentLocale();

    if (justLoggedOut) {
      clearStore();
    }
  }

  showPWAInstallMessage() {
    const isPwaInstallerDisabled = getEnvVar('PWA_DISABLED');

    if (!isPwaInstallerDisabled) {
      const pwaInstallMessageShowed = parseBoolean(localStorage.getItem('pwaInstallMessageShowed'));

      if (pwaInstallMessageShowed) {
        return;
      }

      const pwaInstallElement = document.getElementById('pwa-install');
      pwaInstallElement?.showDialog();
    }
  }

  handleNetworkStatusChange() {
    const { setNetworkStatus } = this.props;

    if (!navigator.onLine) {
      setNetworkStatus(false);
    } else {
      setNetworkStatus(true);
    }
  }

  getBasename() {
    const pathname = get(window, 'location.pathname', '');
    const publicUrl = get(process.env, 'PUBLIC_URL', '');
    const inAppRoute = Object.keys(Urls).some((keyProp) =>
      // eslint-disable-next-line import/namespace
      RegExp(`^${Urls[keyProp]}$`).test(pathname)
    );
    const routePath = pathname.replace(/^([^/]*\/[^/]*\/).*$/, '$1');
    const isRoutePathInvalid = !/^\/.*\/$/.test(routePath);

    return inAppRoute || isRoutePathInvalid ? publicUrl : routePath;
  }

  /**
   * @desc Render the component
   * @returns {string}
   */
  render() {
    const { isLoggedIn, isTokenExpired, isOnline } = this.props;

    return (
      <BrowserRouter basename={this.getBasename()}>
        <ScrollToTop>
          <NotificationDialog />
          <ToastNotifications />
          <Routes />
          {isOnline && <LoginDialog />}
          <StorageSpaceUnavailableDialog />
          <IdpDialog />
          {isLoggedIn && !isTokenExpired && isOnline && <SessionTimeoutWarning />}
        </ScrollToTop>
        <pwa-install
          id="pwa-install"
          manifest-url={`${process.env.PUBLIC_URL}/manifest.json`}
          manual-apple="true"
          manual-chrome="true"
          icon={`${process.env.PUBLIC_URL}/ios/192.png`}
        ></pwa-install>
      </BrowserRouter>
    );
  }
}

/**
 * @param state
 */
function mapStateToProps(state) {
  const { auth, workflow, networkStatus } = state;
  return {
    formIsSubmitting: workflow.formIsSubmitting,
    isLoggedIn: auth.logged_in,
    isTokenExpired: auth.isTokenExpired,
    isLogonUserChanged: auth.isLogonUserChanged,
    isOnline: networkStatus.isOnline,
  };
}

/**
 * @param dispatch
 * @returns {{actions}}
 */
const mapDispatchToProps = {
  clearStore,
  startNotificationPullingSystem,
  startNotificationPullingTimer,
  setNetworkStatus,
  getTranslatedKey,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
